import React, { useState } from "react";
import { Button, Modal, TextAreaField } from "../../../components/ui";
import { appointmentShortDate } from "../appointments";
import type { ClientAppointment } from "../types/appointments.types";

export interface CancelAppointmentModalProps {
  /** The appointment being called off — named in the sentence, by day and kind. */
  appointment: ClientAppointment;
  /** The client's first name, so the dialog says who the call was with. */
  firstName: string;
  /** Set while the cancellation is with the server. */
  busy?: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}

/**
 * Calls an appointment off.
 *
 * Asked before the row changes, because this is the one act on the tab that
 * reaches outside it: an appointment the client has in their own calendar is
 * being taken back. The dialog names the day and who it was with, so a menu
 * opened on the row above is caught here rather than after the fact.
 *
 * The reason is optional and marked so. It is a note for the practitioner's
 * own records — the cancel button is not held closed by it, because a call
 * that has to be dropped is dropped whether or not there is time to write
 * down why.
 */
const CancelAppointmentModal: React.FC<CancelAppointmentModalProps> = ({
  appointment,
  firstName,
  busy = false,
  onClose,
  onConfirm,
}) => {
  const [reason, setReason] = useState("");

  return (
    <Modal
      open
      // Titled as the question it asks. `hideTitle` because the body draws its
      // own heading at the size the design sets it, and `title` still names
      // the dialog to a screen reader.
      title="Cancel this appointment?"
      hideTitle
      onClose={() => !busy && onClose()}
      className="max-w-[500px]"
      footer={
        <>
          {/* "Cancel" beside a button that cancels the appointment would be two
              different cancellations an inch apart — the same trap
              `DeclinePaymentModal` steps around, answered the same way. */}
          <Button variant="secondary" size="md" disabled={busy} onClick={onClose}>
            Keep it
          </Button>
          <Button size="md" variant="danger" loading={busy} onClick={() => onConfirm(reason)}>
            Cancel
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {/* `aria-hidden` because `hideTitle` does not drop the heading, it
            only takes it out of sight — the same words are still in the tree
            as the dialog's accessible name, and this would be the second copy
            of them. What is drawn here is the design's size for it. */}
        <p
          aria-hidden="true"
          className="text-[20px] 3xl:text-[22px] font-medium text-ink"
        >
          Cancel this appointment?
        </p>

        <p className="text-[14px] 3xl:text-[15px] text-ink-muted">
          The {appointmentShortDate(appointment.startsAt)}{" "}
          {appointment.type === "Online" ? "video call" : "in-person appointment"}{" "}
          with <span className="font-medium text-ink">{firstName}</span> will be
          marked <span className="font-medium text-[#FF3B30]">Cancelled</span>.
          It stays in the history - it isn’t deleted.
        </p>

        <div>
          {/* Labelled by hand rather than through the field's own `label`,
              because the word after it is the point: every other control in
              the app marks what is required, and this one has to mark what is
              not. The `<label>` is still a real one, so the box is reached by
              a click on either word. */}
          <label
            htmlFor="appointment-cancel-reason"
            className="block text-[13px] 3xl:text-[14px] text-ink mb-1.5"
          >
            Reason <span className="text-ink-muted">(optional)</span>
          </label>
          <TextAreaField
            id="appointment-cancel-reason"
            value={reason}
            onValueChange={setReason}
            placeholder="Add a note for your records..."
            rows={4}
            disabled={busy}
          />
        </div>
      </div>
    </Modal>
  );
};

export default CancelAppointmentModal;
